
import { useEffect } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { ArrowLeft, FileText, CheckCircle, Link as LinkIcon, RefreshCw, Shield, LayoutTemplate } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';

const KundenservicePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Handle hash navigation after initial scroll
    setTimeout(() => {
      const hash = window.location.hash;
      if (hash) {
        const id = hash.replace('#', '');
        const element = document.getElementById(id);
        if (element) {
          // Add some offset for the navbar
          const yOffset = -100;
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }
    }, 500);
  }, []);

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar />
      
      <main>
        <div id="top" className="relative text-white py-16 px-4">
          {/* Hero background image */}
          <div className="absolute inset-0 z-0">
            <img 
              src="https://github.com/stone1971grey/projekt-bilder/blob/main/vorteil-direkter-zugriff-auf-dokumente.jpg?raw=true"
              alt="Hero background"
              className="w-full h-full object-cover"
            />
            {/* Overlay to ensure text is readable */}
            <div className="absolute inset-0 bg-black/30"></div>
          </div>

          <div className="container mx-auto relative z-10">
            <div className="mb-6">
              <RouterLink to="/#anwendungsfaelle" className="inline-flex items-center text-white hover:text-blue-100 transition-colors">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Zurück zur Übersicht
              </RouterLink>
            </div>
            <div className="flex items-start gap-6">
              <div className="text-white mt-1">
                <FileText className="h-12 w-12" />
              </div>
              <div id="direkter-zugriff-microsoft-365">
                <h1 className="text-3xl md:text-4xl font-bold mb-6">
                  Direkter Zugriff auf ausgewählte Microsoft 365-Dokumente über TYPO3
                </h1>
                <p className="text-xl max-w-3xl">
                  Die Integration von SPTools ermöglicht es, Microsoft 365-Dokumente direkt auf Ihrer TYPO3-Website einzubinden. 
                  Dadurch erhalten Kunden schnellen und einfachen Zugang zu relevanten Informationen, was die Aktualität, 
                  Zuverlässigkeit und Flexibilität Ihrer Inhalte erhöht und die Kundeninteraktion sowie -zufriedenheit verbessert.​
                </p>
              </div>
            </div>
          </div>
        </div>

        <section className="bg-white py-16" id="support">
          <div className="container mx-auto px-6 max-w-6xl">
            
            <div className="mb-16">
              <h3 className="text-2xl font-semibold mb-6">Beispiel: Zugriff auf Produktdatenblätter</h3>
              <p className="mb-6 text-gray-700">Ein Kunde besucht Ihre TYPO3-Website, um sich über Produkte zu informieren. Auf der Produktseite findet er eine Liste mit Produktdatenblättern im PDF-Format. Neben jedem Produkt befindet sich ein Link mit der Bezeichnung „Datenblatt herunterladen".​</p>
              
              <div className="grid gap-6 md:grid-cols-2 my-10">
                <div className="bg-gray-50 p-6 rounded-2xl shadow">
                  <h4 className="text-xl font-semibold mb-2 flex items-center">
                    <span className="bg-blue-100 rounded-full p-1 mr-2 text-blue-600">1</span>
                    Auswahl und Klick auf den Link
                  </h4>
                  <p className="text-gray-600">Der Kunde wählt ein bestimmtes Produkt aus und klickt auf den entsprechenden Link, um das zugehörige Datenblatt direkt herunterzuladen.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-2xl shadow">
                  <h4 className="text-xl font-semibold mb-2 flex items-center">
                    <span className="bg-blue-100 rounded-full p-1 mr-2 text-blue-600">2</span>
                    Direkter Online-Zugriff
                  </h4>
                  <p className="text-gray-600">Dank der Integration erhält der Kunde direkten Zugriff auf das ausgewählte Dokument (Live-Zugriff). Alternativ kann das Dokument auch über die TYPO3-Filelist aus Microsoft 365 synchronisiert und dem Besucher zur Verfügung gestellt werden.</p>
                </div>
              </div>
            </div>

            <div className="mb-10">
              <h3 className="text-2xl font-semibold mb-6">Vorteile des direkten Dokumentenzugriffs</h3>
              
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-2">
                <div className="bg-gray-50 p-6 rounded-2xl shadow hover:shadow-md transition-shadow">
                  <div className="flex items-start">
                    <div className="text-blue-600 mr-4">
                      <LinkIcon className="h-8 w-8" />
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold mb-2">Nahtlose Integration</h4>
                      <p className="text-gray-600">SPTools ermöglicht einen reibungslosen Zugriff auf Microsoft 365-Dokumente über TYPO3, wodurch Kunden einfach und schnell auf relevante Informationen zugreifen können.​</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-2xl shadow hover:shadow-md transition-shadow">
                  <div className="flex items-start">
                    <div className="text-blue-600 mr-4">
                      <Shield className="h-8 w-8" />
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold mb-2">Sicherheit</h4>
                      <p className="text-gray-600">Die Verwendung von SPTools gewährleistet eine sichere und zuverlässige Übertragung von Daten zwischen TYPO3 und Microsoft 365.​</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-2xl shadow hover:shadow-md transition-shadow">
                  <div className="flex items-start">
                    <div className="text-blue-600 mr-4">
                      <RefreshCw className="h-8 w-8" />
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold mb-2">Aktualität und Konsistenz</h4>
                      <p className="text-gray-600">Durch die direkte Verbindung zwischen Ihrer TYPO3-Website und Microsoft 365 über SPTools wird stets das aktuellste und gültige Dokument angezeigt. Kunden können sich daher jederzeit auf die Richtigkeit und Vollständigkeit der bereitgestellten Informationen verlassen.​</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-2xl shadow hover:shadow-md transition-shadow">
                  <div className="flex items-start">
                    <div className="text-blue-600 mr-4">
                      <LayoutTemplate className="h-8 w-8" />
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold mb-2">Flexibilität</h4>
                      <p className="text-gray-600">Mit SPTools können verschiedene Berechtigungsstufen konfiguriert werden, um sicherzustellen, dass Kunden nur auf die Dokumente zugreifen können, für die sie autorisiert sind.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Fazit */}
            <div className="mt-16 bg-blue-50 p-8 rounded-2xl shadow-md text-center border border-blue-100">
              <h4 className="text-xl font-semibold mb-2">Fazit: Optimale Dokumentenzugänglichkeit mit SPTools</h4>
              <p className="text-lg text-gray-700">Direkter Zugriff auf aktuelle Dokumente – sichere Datenübertragung – perfekt integriert in TYPO3 mit <a href="https://sptools.de" target="_blank" rel="noopener noreferrer" className="text-blue hover:underline">SPTools</a></p>
            </div>
          </div>
        </section>

        {/* Connect Chemicals Reference Case - UPDATED LAYOUT */}
        <section className="bg-white py-16" id="referenz-connect-chemicals">
          <div className="container mx-auto px-6 max-w-6xl">
            <h2 className="text-3xl font-bold mb-16 text-center">
              Praxisbeispiel: Microsoft 365-Dokumentenzugriff bei Connect Chemicals
            </h2>

            <div className="mb-12">
              <p className="text-lg text-gray-700 mb-4">
                Für die <strong>Connect Chemicals GmbH</strong> wurde eine individuelle Lösung entwickelt, um den Zugriff auf ausgewählte
                <strong> Microsoft 365-Dokumente direkt über TYPO3</strong> zu ermöglichen.
              </p>
              <p className="text-lg text-gray-700 mb-4">
                Mitarbeitende erhalten dadurch einen sicheren, durchgängigen Zugriff auf zentrale Produktinformationen, Marketingmaterialien und
                technische Daten – ohne das CMS verlassen zu müssen.
              </p>
              <p className="text-lg text-gray-700">
                Die Integration wurde nahtlos in den bestehenden <strong>Product Finder</strong> eingebettet. Die Microsoft-365-Dokumente werden
                dabei über SharePoint gesteuert und gezielt über TYPO3 ausgespielt.
              </p>
            </div>

            <div className="mb-12">
              <img
                src="https://raw.githubusercontent.com/stone1971grey/projekt-bilder/main/CC_1920x1080.jpg"
                alt="Screenshot Microsoft 365 Integration bei Connect Chemicals"
                className="rounded-xl shadow-lg w-full h-auto object-cover opacity-0 animate-fadeIn"
              />
            </div>

            <div className="bg-blue-50 p-6 rounded-xl border-l-4 border-blue-300 text-blue-900">
              <p className="text-lg font-medium">
                Das Ergebnis: Ein zukunftssicheres System, das Microsoft 365 optimal in TYPO3 integriert – für effiziente Zusammenarbeit und
                zentralen Wissenszugriff.
              </p>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default KundenservicePage;
